// SAB-1045: the "W / Fri" designation that replaces the +Add affordance.
//
// FLEX DISCIPLINE (SAB-1005): this swaps a fixed-width icon for VARIABLE text in a cell
// that was never sized for it. `whitespace-nowrap` + `shrink-0` so the badge keeps its
// own width, and the surrounding name column carries `min-w-0` so IT absorbs the
// difference instead of the row overflowing. A `truncate` here would be worse than
// useless: "W / F…" is not a day.
//
// TAP TARGET vs VISUAL. index.css:426 gives every button a 44px minimum, for iOS. The
// +Add it replaces is therefore 44px too -- but it is a ghost button, so nobody sees the
// target. Painting the badge's background on the button itself made that 44px suddenly
// visible as a slab twice the height of the row's other chips. So the button stays the
// (invisible) 44px target and an inner span carries the visible chip. Same reachability,
// correct weight.
import { cn } from '@/lib/utils'
import { waiverBadgeText, waiverBadgeTitle, waiverChipText, type WaiverInfo } from '@/lib/nflWaiverBadge'

interface Props {
  player: WaiverInfo
  /** 'row' = the add-button slot; 'chip' = next to the name on player detail. */
  variant?: 'row' | 'chip'
  onClick?: () => void
  className?: string
}

export function NFLWaiverBadge({ player, variant = 'row', onClick, className }: Props) {
  const text = variant === 'chip' ? waiverChipText(player) : waiverBadgeText(player)
  if (!text) return null
  const title = waiverBadgeTitle(player)

  const chip = cn(
    'inline-flex items-center justify-center whitespace-nowrap shrink-0 rounded',
    'font-bold tabular-nums text-amber-400 bg-amber-400/10 ring-1 ring-amber-400/25',
    variant === 'chip' ? 'text-[11px] px-1.5 py-0.5' : 'text-[10px] px-1.5 py-0.5',
    className,
  )

  // Still a button in the row: the ticket keeps the tap opening bid mode. A span here
  // would make the only route to a claim invisible on the surface where the player is.
  if (onClick) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClick() }}
        title={title}
        aria-label={title}
        className="inline-flex items-center justify-center shrink-0 bg-transparent p-0 group"
      >
        <span className={cn(chip, 'group-hover:bg-amber-400/20 transition-colors')}>{text}</span>
      </button>
    )
  }
  return <span title={title} aria-label={title} className={chip}>{text}</span>
}
